import { Box, Divider, Grid2, Typography } from "@mui/material";
import { GridColDef, GridPaginationModel, GridRowParams } from "@mui/x-data-grid";
import moment from "moment";
import { FC, useEffect, useMemo, useState } from "react";

import { IVehicleLogRowDetails } from ".";
import { AGDataGrid } from "../../components/AGDataGrid";
import { AGDialog } from "../../components/AGDialog";
import EvidenceImage from "../../components/EvidenceImage";
import { useGetAlertEventsQuery } from "../../redux/reducers/alerts.reducer";
import { EVENT_PRODUCT_TYPE_E, IGetAlertEventsRequestData } from "../../types";
import { convertSnakeToTitleCase } from "../../utils/commonUtils";
import { ROWS_PER_PAGE } from "../../utils/constants";

interface IOffenceData {
	id: number;
	timestamp: string;
	location: string;
	violation: string;
	status: "Pending" | "Paid" | "Disputed";
	amount: number;
	vehicle_number: string;
	officer: string;
	description: string;
	evidence_images: string[];
	challan_number: string;
}

interface IPreviousOffencesDialogProps {
	open: boolean;
	vehicleLogDetails: IVehicleLogRowDetails | null;
	onClose: () => void;
}

const PreviousOffencesDialog: FC<IPreviousOffencesDialogProps> = (props) => {
	const { open, vehicleLogDetails, onClose } = props;

	const [selectedOffence, setSelectedOffence] = useState<IOffenceData | null>(null);
	const [activeImage, setActiveImage] = useState<number>(0);
	const [filters, setFilters] = useState<IGetAlertEventsRequestData>({
		page: 1,
		product_type: EVENT_PRODUCT_TYPE_E.RECON,
		number_plate_exact: vehicleLogDetails?.vehicle_number
	});

	// APIS
	// GET ALERT EVENTS
	const {
		data: getAlertEventsResponse,
		isLoading: getAlertEventsLoading,
		isFetching: getAlertEventsFetching
	} = useGetAlertEventsQuery(filters, { skip: !filters.number_plate_exact });

	// Mock data for offences - replace with actual API call
	const offencesData: IOffenceData[] = useMemo(() => {
		if (!getAlertEventsResponse) return [];
		return getAlertEventsResponse.results.map((item) => ({
			id: item.id,
			timestamp: item.timestamp,
			location: item.location_name,
			violation: item.violations.map((violation) => convertSnakeToTitleCase(violation.violation_name)).join(", "),
			status: "Pending",
			amount: 0,
			vehicle_number: item.number_plate,
			officer: item.officer_name,
			description: item.comment || "N/A",
			evidence_images: item.evidence_images,
			challan_number: item.unique_id
		}));
	}, [getAlertEventsResponse]);

	const selectedOffenceLogDetails = useMemo<IVehicleLogRowDetails | null>(() => {
		if (!selectedOffence || !getAlertEventsResponse) return null;
		const logDetails = getAlertEventsResponse.results.find((item) => item.id === selectedOffence.id);
		if (!logDetails) return null;

		const isChallanCreated = logDetails.challan_info?.challans?.length > 0 || false;
		const retryChallan = logDetails.challan_info?.challans?.some((item) => item.status === "failed") || false;

		return {
			id: logDetails.id,
			timestamp: logDetails.timestamp,
			number_plate: logDetails.number_plate,
			location: logDetails.location_name,
			speed_radar: logDetails.radar_speed ? `${logDetails.radar_speed} km/hr` : "",
			speed_vision: logDetails.speed ? `${logDetails.speed} km/hr` : "",
			plate_type: logDetails.vehicle_category ?? "",
			color: logDetails.color,
			color_icon: logDetails.color_icon,
			make: logDetails.vehicle_brand,
			make_icon: logDetails.vehicle_brand_icon,
			model: logDetails.vehicle_model,
			camera: logDetails.site_name,
			class: logDetails.vehicle_class,
			vehicle_class_icon: logDetails.vehicle_class_icon,
			device: logDetails.device.device_name,
			violations: logDetails.violations
				.filter((violationItem) => !!violationItem.violation_name)
				.map((violationItem) => ({
					name: convertSnakeToTitleCase(violationItem.violation_name),
					lane: violationItem.lane_name,
					id: violationItem.id,
					violation_id: violationItem.violation,
					lane_id: violationItem.lane,
					is_speed_related: violationItem.violation_name.toLowerCase().includes("speed")
				})),
			event_id: logDetails.unique_id,
			vehicle_type: logDetails.vehicle_type,
			vehicle_type_icon: "",
			direction: logDetails.direction,
			direction_name: logDetails.direction_name,
			is_bookmarked: logDetails.is_bookmarked,
			ai_meta_data: logDetails.ai_meta_data || "",
			bbox: logDetails.bbox,
			icon: null,
			vehicle_type_name: "",
			vehicle_number: logDetails.number_plate,
			plate_image_uuid: logDetails.plate_evidences?.[0] || "",
			vehicle_images: logDetails.new_evidence_images,
			vehicle_image_frame_id: logDetails.evidence_video_url,
			site_id: Number(logDetails.camera.site_id),
			site_name: logDetails.camera.site_name,
			brand_id: 0,
			brand: "",
			brand_icon: "",
			model_id: 0,
			color_id: 0,
			speed: logDetails.speed ? `${logDetails.speed} km/hr` : "",
			radar_speed: logDetails.radar_speed ? `${logDetails.radar_speed} km/hr` : "",
			vehicle_class_id: 0,
			vehicle_class: "",
			category_id: 0,
			category: "",
			officer: logDetails.officer_name,
			location_coordinates: logDetails.location_co_ordinates,
			location_name: logDetails.location_name,
			crop_coordinates: logDetails.crop_co_ordinates
				? {
						plate: logDetails.crop_co_ordinates.plate,
						violation: logDetails.crop_co_ordinates.violation?.[0] ?? [],
						...(logDetails.crop_co_ordinates.plate_image_name != null && {
							plate_image_name: logDetails.crop_co_ordinates.plate_image_name
						}),
						...(logDetails.crop_co_ordinates.violation_image_name != null && {
							violation_image_name: logDetails.crop_co_ordinates.violation_image_name
						})
				  }
				: { plate: [], violation: [] },
			violation_image: "",
			status: logDetails.validated_status,
			lanes: logDetails.camera.camera_config_roi.map((laneItem) => ({ id: laneItem.id, name: laneItem.name })),
			image_width: logDetails.camera.width,
			image_height: logDetails.camera.height,
			remarks: logDetails.comment || "",
			is_challan_created: isChallanCreated,
			retry_challan: retryChallan,
			challan_status: isChallanCreated ? (retryChallan ? "failed" : "issued") : "pending"
		};
	}, [getAlertEventsResponse, selectedOffence]);

	function getStatusColor(status: string) {
		switch (status) {
			case "Paid":
				return "#4caf50";
			case "Pending":
				return "#ff9800";
			case "Disputed":
				return "#f44336";
			default:
				return "#9e9e9e";
		}
	}

	function handleRowClick(params: GridRowParams<IOffenceData>) {
		setSelectedOffence(params.row || null);
	}

	function handlePageChange(event: GridPaginationModel) {
		setFilters((prev) => ({ ...prev, page: event.page + 1 }));
	}

	function handleChangeActiveImage(value: number) {
		setActiveImage(value);
	}

	// Select first row by default when dialog opens
	useEffect(() => {
		if (open && offencesData.length > 0 && !selectedOffence) {
			setSelectedOffence(offencesData[0]);
			setActiveImage(0);
		}
	}, [open, offencesData, selectedOffence]);

	useEffect(() => {
		if (open && vehicleLogDetails) {
			setFilters((currentFilters) => ({
				...currentFilters,
				page: 1,
				number_plate_exact: vehicleLogDetails.vehicle_number
			}));
		} else {
			setFilters((currentFilters) => ({ ...currentFilters, number_plate_exact: "" }));
		}
	}, [open, vehicleLogDetails]);

	const columns: GridColDef[] = [
		{
			field: "timestamp",
			headerName: "Timestamp",
			minWidth: 175,
			flex: 1,
			sortable: false,
			align: "center",
			headerAlign: "center",
			valueFormatter: (value) => moment(value).format("DD/MM/YYYY hh:mm A")
		},
		{
			field: "violation",
			headerName: "Violation",
			minWidth: 200,
			flex: 1.5,
			sortable: false,
			align: "center",
			headerAlign: "center"
		},
		{
			field: "status",
			headerName: "Status",
			width: 100,
			sortable: false,
			headerAlign: "center",
			renderCell: (params) => (
				<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Box
						sx={{
							backgroundColor: getStatusColor(params.value),
							color: "white",
							padding: "2px 8px",
							borderRadius: "12px",
							width: "fit-content",
							minWidth: "60px"
						}}
					>
						<Typography variant="body2" textAlign="center">
							{params.value}
						</Typography>
					</Box>
				</Box>
			)
		},
		{
			field: "amount",
			headerName: "Amount",
			width: 80,
			sortable: false,
			align: "center",
			headerAlign: "center",
			valueFormatter: (value) => `₹${value}`
		}
	];

	return (
		<AGDialog bgGrey open={open} onClose={onClose} title="Previous Offences" color="primary" maxWidth="xl">
			<Box sx={{ padding: "1rem 0" }}>
				<Grid2 container spacing={2}>
					{/* Left Column - Offence Details */}
					<Grid2 size={{ xs: 12, md: 8 }}>
						<Box className="event-log-details-card full-height flex-column">
							{selectedOffence ? (
								<>
									<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
										Offence Details
									</Typography>

									{selectedOffence.evidence_images.length > 0 && (
										<Grid2 size={{ xs: 12 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<EvidenceImage
													vehicleLogDetails={selectedOffenceLogDetails}
													bbox={selectedOffenceLogDetails?.bbox ?? []}
													violations={selectedOffenceLogDetails?.violations ?? []}
													activeImage={activeImage}
													onChangeActiveImage={handleChangeActiveImage}
												/>
											</Box>
											<Divider sx={{ margin: "1rem 0" }} />
										</Grid2>
									)}

									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, sm: 6 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Challan Number
												</Typography>
												<Typography variant="body1" fontWeight={500}>
													{selectedOffence.challan_number}
												</Typography>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12, sm: 6 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Timestamp
												</Typography>
												<Typography variant="body1" fontWeight={500}>
													{moment(selectedOffence.timestamp).format("DD/MM/YYYY")} at{" "}
													{moment(selectedOffence.timestamp).format("hh:mm A")}
												</Typography>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													District
												</Typography>
												<Typography variant="body1" fontWeight={500}>
													{selectedOffence.location}
												</Typography>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Violation
												</Typography>
												<Typography variant="body1" fontWeight={500}>
													{selectedOffence.violation}
												</Typography>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12, sm: 6 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Status
												</Typography>
												<Box
													sx={{
														backgroundColor: getStatusColor(selectedOffence.status),
														color: "white",
														padding: "4px 12px",
														borderRadius: "16px",
														fontSize: "0.75rem",
														fontWeight: 500,
														textAlign: "center",
														width: "fit-content"
													}}
												>
													{selectedOffence.status}
												</Box>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12, sm: 6 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Fine Amount
												</Typography>
												<Typography variant="body1" fontWeight={500} color="error">
													₹{selectedOffence.amount}
												</Typography>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12, sm: 6 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Vehicle Number
												</Typography>
												<Typography variant="body1" fontWeight={500}>
													{selectedOffence.vehicle_number}
												</Typography>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12, sm: 6 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Issuing Officer
												</Typography>
												<Typography variant="body1" fontWeight={500}>
													{selectedOffence.officer}
												</Typography>
											</Box>
										</Grid2>

										<Grid2 size={{ xs: 12 }}>
											<Divider sx={{ marginBottom: "1rem" }} />
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Typography variant="body2" color="text.secondary">
													Remarks
												</Typography>
												<Typography variant="body1">{selectedOffence.description}</Typography>
											</Box>
										</Grid2>
									</Grid2>
								</>
							) : (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										height: "100%",
										gap: "1rem"
									}}
								>
									<Typography variant="h6" color="text.secondary">
										Select an offence to view details
									</Typography>
									<Typography variant="body2" color="text.secondary" textAlign="center">
										Click on any row in the offences list to see detailed information
									</Typography>
								</Box>
							)}
						</Box>
					</Grid2>

					{/* Right Column - Offences List */}
					<Grid2 size={{ xs: 12, md: 4 }}>
						<Box className="event-log-details-card full-height flex-column">
							<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
								Previous Offences ({offencesData.length})
							</Typography>

							<Box sx={{ flex: 1, minHeight: 0 }}>
								<AGDataGrid
									columns={columns}
									rows={offencesData}
									paginationMode="server"
									paginationModel={{ page: filters.page - 1, pageSize: ROWS_PER_PAGE }}
									onPaginationModelChange={handlePageChange}
									rowCount={getAlertEventsResponse?.count ?? 0}
									loading={getAlertEventsLoading || getAlertEventsFetching}
									hideFooter={(getAlertEventsResponse?.count ?? 0) <= ROWS_PER_PAGE}
									onRowClick={handleRowClick}
									getRowClassName={(params) =>
										`previous-offence-row ${selectedOffence && params.id === selectedOffence.id ? "selected-row" : ""}`
									}
								/>
							</Box>
						</Box>
					</Grid2>
				</Grid2>
			</Box>
		</AGDialog>
	);
};

export default PreviousOffencesDialog;
